/**
 * Centralized performance logger for QuickFill and other hot paths.
 *
 * Usage:
 *   const end = perf.start('QF-Builder', 'buildInstanceParamIndex');
 *   // ... work ...
 *   end();                  // logs elapsed time
 *
 *   perf.mark('QF-Init', 'fetchStart');
 *   perf.measure('QF-Init', 'total', startMs);
 *
 * Toggle at runtime:
 *   (window as any).__PIPEEX_PERF = true;   // enable
 *   (window as any).__PIPEEX_PERF = false;  // disable
 *   (window as any).__PIPEEX_PERF_WARN_STACKS = true; // opt into warning stacks
 *
 * Logging is opt-in so normal Revit workflows are not noisy.
 */

const isTest = process.env.NODE_ENV === 'test';

function isEnabled(): boolean {
  if (isTest) {
    return false;
  }
  if (typeof window !== 'undefined' && (window as any).__PIPEEX_PERF !== undefined) {
    return !!(window as any).__PIPEEX_PERF;
  }
  return false;
}

function formatMs(ms: number): string {
  return ms < 1 ? `${(ms * 1000).toFixed(0)}us` : `${ms.toFixed(1)}ms`;
}

function start(scope: string, label: string): () => void {
  if (!isEnabled()) return () => {};
  const t0 = performance.now();
  return () => {
    const elapsed = performance.now() - t0;
    console.log(`[${scope}] ${label}: ${formatMs(elapsed)}`);
  };
}

function measure(scope: string, label: string, startMs: number, extra?: Record<string, unknown>): void {
  if (!isEnabled()) return;
  const elapsed = performance.now() - startMs;
  const suffix = extra ? ' ' + JSON.stringify(extra) : '';
  console.log(`[${scope}] ${label}: ${formatMs(elapsed)}${suffix}`);
}

function log(scope: string, message: string, extra?: Record<string, unknown>): void {
  if (!isEnabled()) return;
  const suffix = extra ? ' ' + JSON.stringify(extra) : '';
  console.log(`[${scope}] ${message}${suffix}`);
}

function shouldEmitSlowWarningStacks(): boolean {
  return (
    typeof window !== 'undefined' &&
    (window as any).__PIPEEX_PERF_WARN_STACKS === true
  );
}

function warn(scope: string, message: string, extra?: Record<string, unknown>): void {
  const suffix = extra ? ' ' + JSON.stringify(extra) : '';
  console.warn(`[${scope}] ${message}${suffix}`);
}

function slow(
  scope: string,
  label: string,
  startMs: number,
  thresholdMs = 50,
  extra?: Record<string, unknown>
): number {
  const elapsed = performance.now() - startMs;
  if (isTest) {
    return elapsed;
  }

  const overThreshold = elapsed >= thresholdMs;
  // Fast path: skip building and stringifying the payload on hot render/scroll
  // paths when nothing would be logged anyway.
  if (!overThreshold && !isEnabled()) {
    return elapsed;
  }

  const payload = {
    elapsedMs: Number(elapsed.toFixed(1)),
    thresholdMs,
    ...(extra || {}),
  };
  const suffix = ' ' + JSON.stringify(payload);

  if (overThreshold) {
    const emit = shouldEmitSlowWarningStacks() ? console.warn : console.info;
    emit(`[${scope}] Slow ${label}: ${formatMs(elapsed)}${suffix}`);
  } else {
    console.log(`[${scope}] ${label}: ${formatMs(elapsed)}${suffix}`);
  }

  return elapsed;
}

export const perf = { start, measure, log, warn, slow, isEnabled } as const;
